import { alphaSave } from '../ui/alpha-save';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api } from '../api';
import { ErrorState, Loading } from '../ui/State';
type Renewal = {
  id: string;
  contractor_id: string;
  requirement_type: string;
  description: string;
  expires_on: string;
  status: string;
  version: number;
  legal_name: string;
  expired: boolean;
  action_id: string | null;
  can_prepare: boolean;
  threads: { id: string; title: string }[];
};
export function SupplierRenewalsAlpha() {
  const [model, setModel] = useState<{ items: Renewal[]; notice: string; truncated: boolean }>(),
    [error, setError] = useState<unknown>();
  const load = () =>
    api<{ items: Renewal[]; notice: string; truncated: boolean }>(
      '/api/alpha/supplier-renewals',
    ).then(setModel);
  useEffect(() => {
    void load().catch(setError);
  }, []);
  return (
    <>
      <h1>Supplier evidence renewals</h1>
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {!model ? (
        <Loading />
      ) : (
        <>
          <p>{model.notice}</p>
          {model.truncated && <p>Showing the first 200 due requirements.</p>}
          {!model.items.length ? (
            <p>No evidence renewals due in the next 30 days.</p>
          ) : (
            model.items.map((r) => <RenewalCard key={r.id} item={r} reload={load} />)
          )}
        </>
      )}
    </>
  );
}
function RenewalCard({ item, reload }: { item: Renewal; reload: () => Promise<void> }) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(),
    [saved, setSaved] = useState<{ threadId: string } | null>(null);
  const pending = useRef<object | null>(null);
  async function prepare(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    pending.current ??= {
      threadId: new FormData(e.currentTarget).get('thread'),
      expectedVersion: item.version,
      idempotencyKey: crypto.randomUUID(),
    };
    setBusy(true);
    try {
      const r = await alphaSave<{ threadId: string }>(
        pending,
        `/api/alpha/supplier-renewals/${item.id}/prepare`,
        { method: 'POST', body: JSON.stringify(pending.current) },
      );
      await reload();
      pending.current = null;
      setSaved(r);
      setError(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel">
      <h2>
        {item.legal_name} · {item.requirement_type}
      </h2>
      <p>{item.description}</p>
      <p>
        {item.expired ? 'Expired' : 'Expires'} {item.expires_on} · {item.status}
      </p>
      <p>
        {item.expired
          ? 'Existing eligibility checks may prevent new work until replacement evidence is reviewed.'
          : 'Prepare replacement evidence before its expiry date.'}
      </p>
      <p>
        <Link to={`/work/service-providers/${item.contractor_id}`}>
          Review supplier and replacement evidence
        </Link>
        {item.action_id && (
          <>
            {' '}
            · <Link to={`/work/actions/${item.action_id}`}>Renewal review action</Link>
          </>
        )}
      </p>
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {saved && (
        <p role="status">
          Draft prepared.{' '}
          <Link to={`/communications/${saved.threadId}`}>Review supplier correspondence</Link>. No
          message was sent.
        </p>
      )}
      {item.can_prepare && item.threads.length ? (
        <form onSubmit={prepare}>
          <label>
            Supplier correspondence
            <select name="thread" required disabled={busy || !!pending.current}>
              <option value="">Choose linked correspondence</option>
              {item.threads.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
          </label>
          <button disabled={busy}>
            {busy
              ? 'Preparing…'
              : pending.current
                ? 'Retry the same request'
                : 'Prepare renewal request'}
          </button>
        </form>
      ) : (
        <p>
          {!item.threads.length
            ? 'Link supplier correspondence in Communications before preparing a request.'
            : 'A recent request is already prepared. Review it before chasing again.'}
        </p>
      )}
    </section>
  );
}
